import { Trans } from 'react-i18next'
import styled from 'styled-components'

import { ArrowRight2Icon, Button } from '@blade/ui-kit'
import { useNetworkLinks } from '@root/entities/network'
import { boostersFontCss, BuyPremiumIcon } from '@root/entities/premium/ui'
import { useTranslation } from '@root/shared/localization'
import { Edit1Icon } from '@root/shared/ui-kit'

import type { TxHash } from '@root/entities/transaction'
import type { PurchaseBoostersType } from '@root/features/premium'

type Props = {
  type: PurchaseBoostersType
  transactionHash: TxHash
  onClose?: () => void
  onEdit?: () => void
}

export const PaymentDialogSuccess: React.FC<Props> = ({
  type,
  transactionHash,
  onClose,
  onEdit,
}) => {
  const { t } = useTranslation()
  const { openTransactionExplorerLink } = useNetworkLinks()

  const handleOpenInExplorer = () => {
    openTransactionExplorerLink(transactionHash)
  }

  return (
    <Root>
      <ButtonEdit
        fullWidth
        radius="rounded"
        type="button"
        variant="ghost"
        color="gray"
        size="s"
        iconLeft={<Edit1Icon />}
        onClick={onEdit}
      >
        {t('currency.edit_the_token_details')}
      </ButtonEdit>
      <Separator />
      <ButtonCheckTx
        fullWidth
        variant="ghost"
        size="s"
        onClick={handleOpenInExplorer}
      >
        {t('premium.pay.check-tx')}
      </ButtonCheckTx>
      <Note>
        {type === 'boosters' ? (
          <BuyPremiumIcon product="subscription" size={32} sizeIcon={14} />
        ) : (
          <BuyPremiumIcon product="boosters" size={32} />
        )}
        <NoteText>
          <Trans
            i18nKey={
              type === 'boosters'
                ? 'premium.pay.subscription.success'
                : 'premium.pay.boosters.success'
            }
            components={[<span key={0} />]}
          />
        </NoteText>
        <ButtonViewMore
          onClick={() => {
            if (type === 'boosters') {
              onClose?.()
            }
          }}
          variant="soft"
          radius="rounded"
          type="button"
          size="m"
          iconLeft={<ArrowRight2Icon />}
        />
      </Note>
      <ButtonClose
        onClick={onClose}
        fullWidth
        size="s"
        color="primary"
        radius="rounded"
      >
        {t('common.close')}
      </ButtonClose>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  gap: 12px;
`

const ButtonEdit = styled(Button)`
  border: 1px solid var(--background-4);
  color: var(--appearance-inv-1);
`
const ButtonCheckTx = styled(Button)`
  background: transparent;
  color: var(--primary-9);

  justify-self: center;
  font-size: 12px;
  font-weight: 600;

  &:hover {
    background: transparent;
    color: var(--primary-10);
  }
`

const Separator = styled.div`
  opacity: 0.1;
  width: 100%;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    var(--backgroundA-3) 50.83%,
    transparent 100%
  );
`

const Note = styled.div`
  background: var(--appearanceA-7);

  grid-template-columns: auto 1fr auto;
  display: grid;
  justify-content: center;
  align-items: center;

  border-radius: 12px;
  padding: 2px;
  gap: 12px;
  height: 52px;
  padding-inline-start: 10px;
`

const NoteText = styled.p`
  color: var(--text-11);

  font-size: 12px;
  font-style: normal;
  font-weight: 400;

  span {
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;

    ${boostersFontCss}
  }
`

const ButtonClose = styled(Button)`
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  text-transform: capitalize;
`

const ButtonViewMore = styled(Button)`
  background: var(--backgroundA-3);

  height: 100%;
  width: 24px;

  svg {
    color: var(--textA-7);
    transition: color 0.1s ease-in-out;
  }

  &:hover:not(:disabled) {
    background: var(--backgroundA-4);

    svg {
      color: var(--text-11);
    }
  }
`
